import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { setCompanyId } from "@/lib/api";
import { applyBrand, setTitle } from "@/lib/pwa";
import { InstallButton } from "@/components/pwa/InstallButton";
import { BrainLogo, LoginBackdrop } from "@/components/login/LoginShell";
import { LoginCard } from "@/components/login/LoginCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const homeFor = (u) => (u?.is_task_user ? "/work" : "/dashboard");

export default function BrandedLoginPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);

  useEffect(() => { api.get(`/brand/${slug}`).then((r) => { setBrand(r.data); applyBrand(r.data.slug, r.data.name); setTitle(r.data.name, "Sign in"); }).catch(() => setBrand(false)); }, [slug]);
  if (user) return <Navigate to={homeFor(user)} replace />;
  const done = (u) => {
    localStorage.setItem("pnl_login_slug", slug);
    if (!u.is_super_admin) setCompanyId(brand.id);
    navigate(homeFor(u), { replace: true });
  };

  if (brand === null) return <div className="flex min-h-[100dvh] items-center justify-center bg-[#0B1B33]"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>;
  if (!brand) return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center gap-3 bg-[#0B1B33] text-slate-300" data-testid="branded-login-not-found">
      <p>This sign-in page doesn't exist.</p><Link to="/login" className="text-sm text-teal-400 underline">Go to the main sign-in</Link>
    </div>
  );
  return (
    <div className="relative isolate flex min-h-[100dvh] flex-col items-center justify-center px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1.5rem,env(safe-area-inset-top))]" data-testid="branded-login-page">
      <LoginBackdrop />
      <div className="absolute right-4 top-[max(1rem,env(safe-area-inset-top))]"><InstallButton dark /></div>
      <div className="rise-in flex w-full max-w-md flex-col items-center gap-4">
        <LoginCard slug={slug} header={(
          <div className="mb-5 flex flex-col items-center gap-2 border-b border-white/10 pb-5 text-center" data-testid="branded-login-brand">
            {brand.has_logo
              ? <img src={`${API}/brand/${slug}/logo?variant=dark&v=${brand.logo_version}`} alt={`${brand.name} logo`} className="max-h-16 max-w-[220px] object-contain sm:max-h-20 sm:max-w-[260px]" data-testid="branded-login-logo" />
              : <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-500/90 text-xl font-bold text-white" data-testid="branded-login-initials">{brand.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("")}</div>}
            <h1 className="max-w-full truncate text-lg font-semibold tracking-tight text-white sm:text-xl" data-testid="branded-login-name">{brand.name}</h1>
          </div>
        )} title={<h2 className="text-2xl font-bold tracking-tight">Sign in</h2>} subtitle="Use your password, or your PIN on a device you have signed in on before."
          notice={!brand.active && <p className="mt-4 rounded-md border border-amber-300/40 bg-amber-500/15 px-3 py-2 text-sm text-amber-100" data-testid="branded-login-inactive">This company's access is currently deactivated.</p>}
          footer={`Only ${brand.name} accounts can sign in here.`} onSignedIn={done} />
        <div className="flex items-center gap-2 text-[11px] text-slate-400" data-testid="powered-by-appbrain"><BrainLogo className="h-6 w-6" /> Powered by THE BRAIN</div>
      </div>
    </div>
  );
}
